import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { userSelector } from "slices/user/userSlice";
import LinehaulOutboundPlanningService from "app/data/linehaul-outbound-planning/linehaulOutboundPlanningService";
import { LinehaulOutboundPlanningSummary } from "app/data/linehaul-outbound-planning/models";
import { apiQueryAdapter } from "app/data/apiQueryAdapter";
import { ApiResponse } from "app/data/api2";
import { QUERY_KEYS } from "app/data/common/queryKeys";
import { InboundPickupSummaryResponse, PickupSummary } from "app/data/freights/model";

function ensureOkArray<T>(res: ApiResponse<T[] | unknown>): T[] {
  if (typeof res?.ok === "function" && !res.ok()) {
    const raw = typeof res.getError === "function" ? res.getError() : null;
    const message = typeof raw === "string" ? raw : (raw as { message?: string })?.message ?? "Failed to load outbound planning data.";
    throw new Error(message);
  }
  return Array.isArray(res?.data) ? (res.data as T[]) : [];
}

export interface OutboundPlanningMetrics {
  shipments: number;
  pieces: number;
  weight: number;
}

export interface OutboundPlanningGridRow {
  destinationTerminalNumber: number;
  airportCode: string;
  terminalName: string;
  totals: OutboundPlanningMetrics;
  inHouse: OutboundPlanningMetrics;
  inbound: OutboundPlanningMetrics;
  pickupsTotal: OutboundPlanningMetrics;
  pickupsUnassigned: OutboundPlanningMetrics;
  pickupsAssigned: OutboundPlanningMetrics;
  pickupsCompleted: OutboundPlanningMetrics;
}

function metricsFromLinehaulSummary(row: LinehaulOutboundPlanningSummary | undefined): OutboundPlanningMetrics {
  if (!row) return { shipments: 0, pieces: 0, weight: 0 };
  return {
    shipments: row.numberOfShipments ?? 0,
    pieces: row.numberOfPieces ?? 0,
    weight: row.weight ?? 0,
  };
}

function metricsFromPickupSummary(p: PickupSummary | null | undefined): OutboundPlanningMetrics {
  return {
    shipments: p?.totalPickups ?? 0,
    pieces: p?.totalItems ?? 0,
    weight: p?.totalWeight ?? 0,
  };
}

function sumMetrics(...parts: OutboundPlanningMetrics[]): OutboundPlanningMetrics {
  return parts.reduce(
    (acc, m) => ({
      shipments: acc.shipments + m.shipments,
      pieces: acc.pieces + m.pieces,
      weight: acc.weight + m.weight,
    }),
    { shipments: 0, pieces: 0, weight: 0 }
  );
}

function getTerminalMeta(
  ih: LinehaulOutboundPlanningSummary | undefined,
  ib: LinehaulOutboundPlanningSummary | undefined,
  pk: InboundPickupSummaryResponse | undefined
): { airportCode: string; terminalName: string } {
  if (pk?.serviceCenter) {
    return {
      airportCode: pk.serviceCenter.airportCode ?? "—",
      terminalName: pk.serviceCenter.terminalName ?? "—",
    };
  }
  const row = ih ?? ib;
  return {
    airportCode: row?.destinationTerminalAirportCode ?? "—",
    terminalName: row?.destinationTerminalName ?? "—",
  };
}

export function useOutboundPlanningGridData() {
  const userState = useSelector(userSelector);
  const originTerminalNumber = userState.activeTerminal?.id;
  const enabled = typeof originTerminalNumber === "number";

  const results = useQueries({
    queries: [
      {
        queryKey: [QUERY_KEYS.OUTBOUND_PLANNING, "inHouseFreight", originTerminalNumber],
        enabled,
        retry: false,
        queryFn: async () => {
          const svc = LinehaulOutboundPlanningService.getInstance();
          const res = await svc.getInHouseFreightSummary({
            originTerminalNumber: originTerminalNumber as number,
          });
          return ensureOkArray<LinehaulOutboundPlanningSummary>(res);
        },
      },
      {
        queryKey: [QUERY_KEYS.OUTBOUND_PLANNING, "outboundInboundFreight", originTerminalNumber],
        enabled,
        retry: false,
        queryFn: async () => {
          const svc = LinehaulOutboundPlanningService.getInstance();
          const res = await svc.getInboundFreightSummary({
            originTerminalNumber: originTerminalNumber as number,
          });
          return ensureOkArray<LinehaulOutboundPlanningSummary>(res);
        },
      },
      {
        queryKey: [QUERY_KEYS.OUTBOUND_PLANNING, QUERY_KEYS.PICKUP_SUMMARY, originTerminalNumber],
        enabled,
        retry: false,
        queryFn: async () => {
          const response = await apiQueryAdapter.query<InboundPickupSummaryResponse[]>(
            "/pickups/inbound/summary",
            "GET",
            { params: { originTerminal: originTerminalNumber } }
          );
          return Array.isArray(response?.data) ? response.data : [];
        },
      },
    ],
  });

  const [inHouseQ, inboundQ, pickupsQ] = results;

  const rows = useMemo((): OutboundPlanningGridRow[] => {
    const inHouseList = Array.isArray(inHouseQ.data) ? inHouseQ.data : [];
    const inboundList = Array.isArray(inboundQ.data) ? inboundQ.data : [];
    const pickupsList = Array.isArray(pickupsQ.data) ? pickupsQ.data : [];

    const inHouseMap = new Map<number, LinehaulOutboundPlanningSummary>();
    for (const r of inHouseList) {
      const n = r.destinationTerminalNumber;
      if (typeof n === "number") inHouseMap.set(n, r);
    }

    const inboundMap = new Map<number, LinehaulOutboundPlanningSummary>();
    for (const r of inboundList) {
      const n = r.destinationTerminalNumber;
      if (typeof n === "number") inboundMap.set(n, r);
    }

    const pickupMap = new Map<number, InboundPickupSummaryResponse>();
    for (const r of pickupsList) {
      const n = r?.serviceCenter?.terminalNumber;
      if (typeof n === "number") pickupMap.set(n, r);
    }

    const terminalNums = new Set<number>();
    inHouseMap.forEach((_, k) => terminalNums.add(k));
    inboundMap.forEach((_, k) => terminalNums.add(k));
    pickupMap.forEach((_, k) => terminalNums.add(k));

    const sorted = Array.from(terminalNums).sort((a, b) => {
      const metaA = getTerminalMeta(inHouseMap.get(a), inboundMap.get(a), pickupMap.get(a));
      const metaB = getTerminalMeta(inHouseMap.get(b), inboundMap.get(b), pickupMap.get(b));
      return metaA.terminalName.localeCompare(metaB.terminalName);
    });

    return sorted.map((destinationTerminalNumber) => {
      const ih = inHouseMap.get(destinationTerminalNumber);
      const ib = inboundMap.get(destinationTerminalNumber);
      const pk = pickupMap.get(destinationTerminalNumber);

      const inHouse = metricsFromLinehaulSummary(ih);
      const inbound = metricsFromLinehaulSummary(ib);

      const pickupsUnassigned = metricsFromPickupSummary(pk?.unassignedSummary);
      const pickupsAssigned = metricsFromPickupSummary(pk?.assignedSummary);
      const pickupsCompleted = metricsFromPickupSummary(pk?.completedSummary);
      const pickupsTotal = sumMetrics(pickupsUnassigned, pickupsAssigned, pickupsCompleted);

      const totals = sumMetrics(inHouse, inbound, pickupsTotal);

      const meta = getTerminalMeta(ih, ib, pk);

      return {
        destinationTerminalNumber,
        airportCode: meta.airportCode,
        terminalName: meta.terminalName,
        totals,
        inHouse,
        inbound,
        pickupsTotal,
        pickupsUnassigned,
        pickupsAssigned,
        pickupsCompleted,
      };
    });
  }, [inHouseQ.data, inboundQ.data, pickupsQ.data]);

  const isLoading = results.some((r) => r.isLoading);
  const isFetching = results.some((r) => r.isFetching);
  const error = results.find((r) => r.error)?.error;

  return {
    rows,
    isLoading,
    isFetching,
    error,
    enabled,
  };
}
